import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import { useTheme } from '@mui/system';

interface PaginationTopProps {
  totalDeals: number;
  minDealsToPaginate: number;
  startIndex: number;
  endIndex: number;
  itemsPerPage: number;
  filteredString: { short?: string; long?: string };
  dealCountStr: string;
  dealCountLongStr: string;
  isFiltered: boolean;
  hasNoItems: boolean;
  handleItemsPerPageChange: (event: SelectChangeEvent<number>) => void;
}

const PerPageControl = ({
  itemsPerPage,
  handleItemsPerPageChange
}: {
  itemsPerPage: number;
  handleItemsPerPageChange: (event: SelectChangeEvent<number>) => void;
}) => {
  const theme = useTheme();

  return (
    <FormControl size="small" sx={{ minWidth: '70px' }}>
      <InputLabel
        id="display-per-page"
        sx={{
          backgroundColor: theme.palette.background.paper,
          padding: '0 4px'
        }}
      >
        Per Page
      </InputLabel>
      <Select
        labelId="display-per-page"
        value={itemsPerPage}
        onChange={handleItemsPerPageChange}
        size="small"
        variant="outlined"
      >
        <MenuItem value={10}>10</MenuItem>
        <MenuItem value={20}>20</MenuItem>
        <MenuItem value={30}>30</MenuItem>
        <MenuItem value={50}>50</MenuItem>
        <MenuItem value={100}>100</MenuItem>
      </Select>
    </FormControl>
  );
};

const outerPaginationStyle = {
  flexDirection: 'column',
  gap: 0.5,
  '@media (max-width: 350px)': {
    gap: 2
  }
};

const innerPaginationBoxStyle = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  '@media (max-width: 310px)': {
    justifyContent: 'center',
    alignItems: 'center'
  },
  '@media (min-width: 310px)': {
    flexDirection: 'row',
    justifyContent: 'space-between'
  }
};

const innerBoxStyle = {
  width: '100%',
  '@media (max-width: 310px)': {
    padding: 1.5
  }
};

const labelStyle = {
  fontWeight: 'bold'
};

const paginationLabelStyle = {
  fontWeight: 'bold',
  display: 'block',
  '@media (min-width: 310px)': {
    display: 'none'
  }
};

const mobileLabelStyle = {
  fontWeight: 'bold',
  display: 'none',
  '@media (min-width: 350px)': {
    display: 'block',
    marginRight: 1
  }
};

const pageControlStyle = {
  '@media (max-width: 350px)': {
    paddingBottom: 1.5,
    justifyContent: 'end',
    width: '100%'
  }
};

const PaginationTop = ({
  totalDeals,
  minDealsToPaginate,
  startIndex,
  endIndex,
  itemsPerPage,
  filteredString,
  dealCountStr,
  dealCountLongStr,
  isFiltered,
  hasNoItems,
  handleItemsPerPageChange
}: PaginationTopProps) => {
  if ((isFiltered && hasNoItems) || totalDeals <= minDealsToPaginate) {
    return null;
  }
  if (totalDeals <= minDealsToPaginate && totalDeals > 1) {
    return (
      <Box mt={1}>
        <Typography variant="body2" textAlign="left" sx={labelStyle}>
          {startIndex + 1}–{endIndex} of{' '}
          {filteredString?.long || dealCountLongStr}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="100%"
      mt={1}
      sx={outerPaginationStyle}
    >
      <Box sx={innerPaginationBoxStyle}>
        <Box display="inline-block" sx={innerBoxStyle}>
          <Typography variant="body2" sx={paginationLabelStyle}>
            {startIndex + 1}–{endIndex} of{' '}
            {filteredString?.short || dealCountStr}
          </Typography>
          <Typography variant="body2" sx={mobileLabelStyle}>
            {startIndex + 1}–{endIndex} of{' '}
            {filteredString?.long || dealCountLongStr}
          </Typography>
        </Box>
        <Box display="flex" sx={pageControlStyle}>
          <PerPageControl
            itemsPerPage={itemsPerPage}
            handleItemsPerPageChange={handleItemsPerPageChange}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PaginationTop;
